import moment from 'moment';

// "Market today" on Home > Overview: today's board trades (org.eggs.get_trades 'current') per
// egg-type key on one shared dock-price axis, beside the previous report ('report11').
//
// - One row per key, the board's key and grouping (boardOptions' eggType).
// - DockPrice (the offer's price at the seller's dock, freight excluded) is the comparable price
//   across lanes and pickups; the weekly summary's SalePrice (the bid's) includes freight to the
//   buyer. Hence the axis title "Dock price".
// - Every get_trades row is one trade. The parts of a combo are separate rows with their own
//   trade number, egg type and price, sharing a `Combo` id; the board counts every part
//   (Trades' group count and "Total Trades", the web board's `trades.length`), and so does
//   this. Only the login credits count a combo once.
// - A formula-priced trade (MarketRegion set, as compare.ts and the trade detail read it) is
//   counted apart ("+2 formula") and kept out of the range, median and change. The app leaves it
//   off the price axis; the web draws it hollow at its flat price when it has one
//   (formulaFlatPrice).
// - A trade with no usable dock price (missing, 0 or negative) stays off the axis too and is
//   counted apart ("+1 unpriced"), so a row's counts always add up to the board's group count.
//
// Ported from the mobile app's src/services/marketToday.ts. No Angular here, so Node can test it
// (marketToday.test.mjs).

// The two helpers it imports from the app's src/boardOptions.ts. CACompliant is compared loosely
// on purpose: the string '1' counts as compliant too.

/** A row's egg-type key as the boards group and title it: "CA " when CA compliant, then type,
 *  colour and class (e.g. "CA PGL W XL"). */
export const eggType = row =>
  `${row.CACompliant == 1 ? 'CA ' : ''}${row.EggType} ${row.EggColor} ${row.EggClass}`;
/** Your trades carry who you are on them. */
export const isOwnTrade = t => !!(t.BuyerID || t.SellerID);

/**
 * The fields of a get_trades row this reads. DockPrice arrives as a numeric string.
 * @typedef {Object} MarketTrade
 * @property {number|string} CACompliant
 * @property {string} EggType
 * @property {string} EggColor
 * @property {string} EggClass
 * @property {number|string|null} DockPrice
 * @property {number|string|null} [SalePrice]
 * @property {number|string|null} [Market] A formula trade's adjustment off its quote, the buyer's (delivered).
 * @property {string|null} [MarketRegion]
 * @property {string|null} [MarketSize]
 * @property {string|null} [BuyerID]
 * @property {string|null} [SellerID]
 * @property {number} [s1]
 * @property {number} [s2]
 * @property {number} [s3]
 */

/** @typedef {'bought'|'sold'} OwnSide */

/**
 * One trade on the price axis. `side` is set on the member's own trades.
 * @typedef {Object} PricePoint
 * @property {number} price
 * @property {OwnSide|null} side
 */

/**
 * A formula trade at its flat price, with the formula it was priced by ("-2.00 SE LG").
 * @typedef {Object} FormulaPoint
 * @property {number} price
 * @property {OwnSide|null} side
 * @property {number} market The adjustment off the quote at the dock, so it adds up to `price`.
 * @property {string} region
 * @property {string} size
 */

/**
 * @typedef {Object} MarketRow
 * @property {string} key
 * @property {PriceBasis} basis The price the row's prices are on.
 * @property {number} count Every trade of the key, as the board counts its group: what "busiest"
 *   ranks by.
 * @property {number} priced Flat-priced trades on the axis: the range and median are theirs.
 * @property {number} formula Formula-priced trades, kept out of the range, median and change.
 * @property {FormulaPoint[]} formulaPoints The formula-priced trades that have a flat price,
 *   cheapest first: drawn on the axis (hollow), but not in min, max, median or delta. Web-only.
 * @property {number} unpriced Other trades without a usable dock price, off the axis:
 *   count - priced - formula.
 * @property {PricePoint[]} points The priced trades, cheapest first.
 * @property {number|null} min
 * @property {number|null} max
 * @property {number|null} median
 * @property {number} bought The member's own trades of the key (formula ones too), as the board's
 *   "You Bought/Sold".
 * @property {number} sold The same, sold.
 * @property {number|null} delta The median's change since the previous report; null unless both
 *   days have 2+ priced trades.
 */

export const isFormulaPriced = t => !!(t.MarketRegion && t.MarketRegion.trim());

/**
 * A formula trade's flat price at the dock, or null. A market-priced bid/offer stores its price as
 * the market quote plus its adjustment (the ticket's "37.75 (-53.25 SE LG)"), so a formula trade's
 * DockPrice is already that flat price. A market without a quote leaves the price as the bare
 * adjustment, and then SalePrice equals Market: that is no price, and it would drag the axis
 * toward 0, as would the 0 or negative DockPrice some formula trades carry. Web-only: the app
 * leaves every formula trade off the axis.
 * On the sale side (basis 'sale') it is the SalePrice, the buyer's delivered flat price.
 * @param {MarketTrade} t
 * @param {PriceBasis} [basis]
 * @returns {number|null}
 */
export function formulaFlatPrice(t, basis = 'dock') {
  if (!isFormulaPriced(t)) {
    return null;
  }
  const price = priceOf(t, basis);
  const bareAdjustment = isNumber(t.SalePrice) && isNumber(t.Market)
    && Math.abs(Number(t.SalePrice) - Number(t.Market)) < 0.005;
  return price !== null && !bareAdjustment ? price : null;
}

/**
 * A formula trade's adjustment at the dock. Trades.Market is the bid's, which goes with SalePrice
 * (delivered); the dock's is that less the freight, SalePrice - DockPrice, as the trade detail
 * (member-account-trade.js) works it out, so quote + adjustment adds up to the dot's price.
 */
const dockAdjustment = t => Number(t.Market || 0)
  - (isNumber(t.SalePrice) && isNumber(t.DockPrice) ? Number(t.SalePrice) - Number(t.DockPrice) : 0);

/** A formula trade's adjustment on the side shown: Market itself on the sale side (delivered). */
const adjustment = (t, basis) => (basis === 'sale' ? Number(t.Market || 0) : dockAdjustment(t));

/** A field that holds a number (the server sends prices as numeric strings). */
const isNumber = v => v !== null && v !== undefined && String(v).trim() !== '' && Number.isFinite(Number(v));

/**
 * Which price the card shows: 'dock', the offer's price at the seller's dock (the app's, and the
 * default here), or 'sale', the buyer's delivered price, freight included. Web-only: the card
 * defaults to sale and has a toggle.
 * @typedef {'dock'|'sale'} PriceBasis
 */

/**
 * A trade's price on `basis` as a number, or null when it isn't a usable one: missing, or 0 or
 * less (a flat trade's prices are its bid's and offer's, never 0 or negative), which would
 * stretch the axis.
 * @param {MarketTrade} t
 * @param {PriceBasis} [basis]
 * @returns {number|null}
 */
export function priceOf(t, basis = 'dock') {
  const v = basis === 'sale' ? t.SalePrice : t.DockPrice;
  if (v === null || v === undefined || String(v).trim() === '') {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A dock price as a number, or null when it isn't a usable one (priceOf). */
export const dockPrice = t => priceOf(t, 'dock');

/**
 * Which way a trade is the member's. A trade between two of the member's own companies (the NUCAL
 * group's logins see ROCKY, SCAEGG and NUCAL as one) carries both ids: it is marked bought, as the
 * board's own marker says "You bought" first, while the row's bought and sold counts both count it.
 */
export const ownSide = t => (t.BuyerID ? 'bought' : t.SellerID ? 'sold' : null);

/** The median, or null for none. */
export function median(values) {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// A change is rounded to the precision prices are shown with (formatPrice: at most 3 decimals), so
// its sign agrees with what it reads as: 0.0004 is "no change", not "▲ 0.00¢". It also drops float
// noise (0.3 - 0.1).
const round3 = n => Math.round(n * 1000) / 1000 || 0;

/** A trade's key: the board's (eggType), with the padding of the server's fixed-width columns
 *  ("PGL ", "W ") collapsed, so it reads "PGL W XL". */
export const marketKey = t => eggType(t).replace(/\s+/g, ' ').trim();

/** A trade's price in the range and median, or null (formula-priced, or no usable price). */
const axisPrice = (t, basis) => (isFormulaPriced(t) ? null : priceOf(t, basis));

/** Priced trades of each key: the previous report's medians, on the same basis as today's. */
function pricesByKey(trades, basis) {
  const byKey = new Map();
  trades.forEach(t => {
    const price = axisPrice(t, basis);
    if (price === null) {
      return;
    }
    const key = marketKey(t);
    byKey.set(key, [...(byKey.get(key) || []), price]);
  });
  return byKey;
}

/** Trades needed on both days before a median change is shown. */
export const MIN_FOR_DELTA = 2;

/**
 * Today's trades as one row per key, busiest first (then in the board's order). `previous` is the
 * previous report's trades, or null when it isn't known (no change is shown then). `basis` is the
 * price shown, dock (the app's) or sale (web-only); both days are read on it.
 * @param {MarketTrade[]} today
 * @param {MarketTrade[]|null} previous
 * @param {PriceBasis} [basis]
 * @returns {MarketRow[]}
 */
export function marketRows(today, previous, basis = 'dock') {
  const groups = new Map();
  today.forEach(t => {
    const key = marketKey(t);
    groups.set(key, [...(groups.get(key) || []), t]);
  });
  const before = previous ? pricesByKey(previous, basis) : new Map();

  const rows = [...groups.entries()].map(([key, trades]) => {
    const points = [];
    const formulaPoints = [];
    let formula = 0;
    trades.forEach(t => {
      const price = axisPrice(t, basis);
      const side = isOwnTrade(t) ? ownSide(t) : null;
      if (isFormulaPriced(t)) {
        formula++;
        const flat = formulaFlatPrice(t, basis);
        if (flat !== null) {
          formulaPoints.push({price: flat, side, market: adjustment(t, basis),
            region: (t.MarketRegion || '').trim(), size: (t.MarketSize || '').trim()});
        }
      } else if (price !== null) {
        points.push({price, side});
      }
    });
    points.sort((a, b) => a.price - b.price);
    formulaPoints.sort((a, b) => a.price - b.price);
    const prices = points.map(p => p.price);
    const med = median(prices);
    const previousPrices = before.get(key) || [];
    const previousMedian = median(previousPrices);
    const delta = med !== null && previousMedian !== null
      && prices.length >= MIN_FOR_DELTA && previousPrices.length >= MIN_FOR_DELTA
      ? round3(med - previousMedian) : null;
    const first = trades[0];
    const row = {
      key,
      basis,
      count: trades.length,
      priced: points.length,
      formula,
      unpriced: trades.length - points.length - formula,
      points,
      formulaPoints,
      min: prices.length ? prices[0] : null,
      max: prices.length ? prices[prices.length - 1] : null,
      median: med,
      bought: trades.filter(t => t.BuyerID).length,
      sold: trades.filter(t => t.SellerID).length,
      delta,
    };
    // The board's own order for a key (SortOrderType/Color/Class), after the busiest-first rank.
    const order = [first.s1 ?? 0, first.s2 ?? 0, first.s3 ?? 0];
    return {row, order};
  });

  return rows
    .sort((a, b) => b.row.count - a.row.count
      || a.order[0] - b.order[0] || a.order[1] - b.order[1] || a.order[2] - b.order[2]
      || a.row.key.localeCompare(b.row.key))
    .map(({row}) => row);
}

/** How many of the busiest keys the card shows before "Show all". */
export const BUSIEST = 5;

/**
 * The rows the card shows: the busiest, plus every key the member traded, in rank order.
 * @param {MarketRow[]} rows
 * @param {boolean} expanded
 * @returns {MarketRow[]}
 */
export function visibleRows(rows, expanded, busiest = BUSIEST) {
  return expanded ? rows : rows.filter((row, index) => index < busiest || row.bought + row.sold > 0);
}

/**
 * The lowest and highest dock price over the rows, or null when none has a price.
 * @param {MarketRow[]} rows
 * @returns {[number, number]|null}
 */
export function priceRange(rows) {
  // Every price drawn: the flat trades' range, and (web-only) the formula trades' flat prices.
  const prices = rows.flatMap(r => [
    ...(r.min !== null && r.max !== null ? [r.min, r.max] : []),
    ...(r.formulaPoints || []).map(p => p.price),
  ]);
  if (!prices.length) {
    return null;
  }
  return [Math.min(...prices), Math.max(...prices)];
}

/**
 * @typedef {Object} Axis
 * @property {number} lo The axis' ends: the prices with a margin, or (when that gives no good
 *   ticks) round ticks.
 * @property {number} hi
 * @property {number[]} ticks Round values between lo and hi, evenly spaced.
 */

const NICE_STEPS = [1, 2, 2.5, 5];
/** The smallest span an axis gets, absolute and as a share of the price: near-identical prices
 *  get room instead of a hairline axis that blows up a tenth of a cent. */
const MIN_SPAN = 0.5;
const MIN_SPAN_SHARE = 0.02;
/** The margin past the cheapest and dearest price, as a share of the span. */
const MARGIN = 0.05;

/** Multiples of `step` from lo to hi (inclusive, with float slack). */
function multiples(lo, hi, step) {
  const first = Math.ceil(lo / step - 1e-9);
  const last = Math.floor(hi / step + 1e-9);
  const ticks = [];
  for (let i = first; i <= last; i++) {
    ticks.push(Number((i * step).toFixed(10)));
  }
  return ticks;
}

/**
 * An axis for prices from min to max with 3 to `maxTicks` round ticks (2 when `maxTicks` is 2),
 * spaced 1, 2, 2.5 or 5 times a power of 10. The axis is the prices plus a small margin, the ticks
 * where round values fall inside it, so the prices fill most of the strip. Only when no step gives
 * enough ticks inside does an end move out to a round value, whichever way loses the least room.
 * @returns {Axis}
 */
export function niceTicks(min, max, maxTicks = 4) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return {lo: 0, hi: 1, ticks: [0, 1]};
  }
  const most = Math.max(2, Math.floor(maxTicks));
  const fewest = Math.min(3, most);
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  const minSpan = Math.max(MIN_SPAN, Math.abs((lo + hi) / 2) * MIN_SPAN_SHARE);
  if (hi - lo < minSpan) {
    const mid = (lo + hi) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }
  const data = hi - lo;
  lo -= data * MARGIN;
  hi += data * MARGIN;

  // Every candidate step, smallest first: from one that gives too many ticks to one past the span.
  const steps = [];
  const exponent = Math.floor(Math.log10((hi - lo) / (most + 1))) - 1;
  for (let e = exponent; e <= exponent + 4; e++) {
    NICE_STEPS.forEach(m => steps.push(m * Math.pow(10, e)));
  }

  let best = null;
  let bestCover = -1;
  for (const step of steps) {
    // The ends as they are, or moved out to the next round value.
    const los = [lo, Math.floor(lo / step + 1e-9) * step];
    const his = [hi, Math.ceil(hi / step - 1e-9) * step];
    for (const l of los) {
      for (const h of his) {
        const ticks = multiples(l, h, step);
        const cover = data / (h - l);
        // More cover wins; on a tie, the smaller step (more ticks) found first stays.
        if (ticks.length >= fewest && ticks.length <= most && cover > bestCover + 1e-9) {
          best = {lo: Number(l.toFixed(10)), hi: Number(h.toFixed(10)), ticks};
          bestCover = cover;
        }
      }
    }
  }
  return best ?? {lo, hi, ticks: [lo, hi]};
}

/**
 * A value's x on the axis, `inset` in from both ends of `width` (room for the dots).
 * @param {number} value
 * @param {Axis} axis
 * @param {number} width
 * @param {number} [inset]
 */
export function scaleX(value, axis, width, inset = 0) {
  const span = axis.hi - axis.lo || 1;
  return inset + (value - axis.lo) / span * Math.max(0, width - 2 * inset);
}

// One formatter for every price (formatHelpers.formatNumber(n, 2, 3) builds one per call).
const PRICE = new Intl.NumberFormat('en', {minimumFractionDigits: 2, maximumFractionDigits: 3});

/** A price as the board shows one: 2 decimals, a third when there is one. */
export const formatPrice = n => PRICE.format(n);

/** A price change as the card shows it: board prices are in cents, so with a cents sign ("2.00¢"). */
export const formatCents = n => formatPrice(n) + '¢';

const trades = n => `${n} trade${n === 1 ? '' : 's'}`;
/** "3 trades", "1 bid", "2 offers": a row's count in what it counts (web: bids and offers too). */
const counted = (n, row) => `${n} ${row.noun || 'trade'}${n === 1 ? '' : 's'}`;

/**
 * "14 trades", "1 trade +1 unpriced", "2 unpriced": the parts always add up to the board's count
 * for the key. Web-only: market-based (formula) trades are trades here too; the app counts them
 * apart ("12 trades +2 formula"). Their empty circles tell them apart on the strip.
 * @param {MarketRow} row
 * @returns {string}
 */
export function countLabel(row) {
  const priced = row.priced + row.formula;
  const parts = [
    priced ? counted(priced, row) : '',
    row.unpriced ? `${row.unpriced} unpriced` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' +') : counted(row.count, row);
}

/**
 * "▲ 1.50¢", "▼ 0.25¢", "no change", or null when there's no change to show.
 * @param {MarketRow} row
 * @returns {string|null}
 */
export function deltaLabel(row) {
  if (row.delta === null) {
    return null;
  }
  return row.delta === 0 ? 'no change' : `${row.delta > 0 ? '▲' : '▼'} ${formatCents(Math.abs(row.delta))}`;
}

const REPORT_DATE_FORMATS = ['MM/DD/YY', 'M/D/YY'];
const reportDate = date => moment(date || '', REPORT_DATE_FORMATS, true);

/**
 * The Trades page's `report` for the previous report: 'report11' when its date is the weekday
 * before `now`, otherwise the date itself ("2026-10-09", after a holiday). That is how the page
 * names the day once it is loaded; opened under the other name it would move to this one.
 * @param {string|null|undefined} previous The report's date as get_trades sends it ("m/d/y").
 * @param {Date} [now]
 * @returns {string}
 */
export function previousReportParam(previous, now = new Date()) {
  const date = reportDate(previous);
  if (!date.isValid()) {
    return 'report11';
  }
  const weekdayBefore = moment(now).subtract(1, 'day');
  while (weekdayBefore.day() === 0 || weekdayBefore.day() === 6) {
    weekdayBefore.subtract(1, 'day');
  }
  return date.isSame(weekdayBefore, 'day') ? 'report11' : date.format('YYYY-MM-DD');
}

/**
 * What the previous report is called next to today: "yesterday", or its weekday ("Friday")
 * after a weekend. Both dates as get_trades sends them ("m/d/y"); "the previous day" otherwise.
 * @param {string|null|undefined} today
 * @param {string|null|undefined} previous
 * @returns {string}
 */
export function previousDayName(today, previous) {
  const a = reportDate(today);
  const b = reportDate(previous);
  if (!a.isValid() || !b.isValid()) {
    return 'the previous day';
  }
  return a.diff(b, 'days') === 1 ? 'yesterday' : b.format('dddd');
}

/**
 * Whether the previous report is of an earlier day than today's board. The server works report11
 * out from its own clock when the call lands, so one answered just after midnight, for a board
 * fetched just before, is the board's own day: no comparison. Dates that don't parse are only
 * told apart by being different.
 * @param {string|null|undefined} today
 * @param {string|null|undefined} previous
 * @returns {boolean}
 */
export function isEarlierReport(today, previous) {
  const a = reportDate(today);
  const b = reportDate(previous);
  if (a.isValid() && b.isValid()) {
    return b.isBefore(a, 'day');
  }
  return (previous ?? null) !== (today ?? null);
}

/**
 * The row as one sentence for a screen reader, e.g. "PGL W XL: 12 trades, dock price 43.00 to
 * 49.00 cents, median 45.00, up 1.50 cents from yesterday, you bought 2".
 * @param {MarketRow} row
 * @param {string} [previousDay]
 * @returns {string}
 */
export function rowSentence(row, previousDay = 'yesterday') {
  const parts = [];
  // Web-only: market-based (formula) trades are counted as trades, and said apart after the flat
  // trades' prices: "2 market-based at 41.75 to 50.50 cents", "5 market-based (2 at 44.00 cents)",
  // or without a quote, "5 market-based with no quoted price".
  const priced = row.priced + row.formula;
  if (priced) {
    parts.push(counted(priced, row));
    if (row.unpriced) {
      parts.push(`${row.unpriced} more without a price`);
    }
  } else if (row.unpriced) {
    parts.push(`${counted(row.unpriced, row)} without a price`);
  }
  if (row.min !== null && row.max !== null && row.median !== null) {
    if (row.min === row.max) {
      parts.push(`${row.basis || 'dock'} price ${formatPrice(row.min)} cents`);
    } else {
      parts.push(`${row.basis || 'dock'} price ${formatPrice(row.min)} to ${formatPrice(row.max)} cents`, `median ${formatPrice(row.median)}`);
    }
  }
  if (row.formula) {
    const flat = (row.formulaPoints || []).map(p => p.price);
    const at = !flat.length ? ''
      : formatPrice(flat[0]) + (flat[flat.length - 1] !== flat[0] ? ` to ${formatPrice(flat[flat.length - 1])}` : '') + ' cents';
    parts.push(!flat.length ? `${row.formula} market-based with no quoted price`
      : flat.length < row.formula ? `${row.formula} market-based (${flat.length} at ${at})`
        : `${row.formula} market-based at ${at}`);
  }
  if (row.delta !== null) {
    parts.push(row.delta === 0
      ? `unchanged from ${previousDay}`
      : `${row.delta > 0 ? 'up' : 'down'} ${formatPrice(Math.abs(row.delta))} cents from ${previousDay}`);
  }
  // A bid row's own entries are "you bid 1", an offer row's "you offered 1"; a trade row's are
  // bought or sold.
  const ownVerb = {bid: 'bid', offer: 'offered'}[row.noun];
  const own = ownVerb
    ? [row.bought + row.sold ? `you ${ownVerb} ${row.bought + row.sold}` : ''].filter(Boolean)
    : [row.bought ? `you bought ${row.bought}` : '', row.sold ? `you sold ${row.sold}` : ''].filter(Boolean);
  if (own.length) {
    parts.push(own.join(' and '));
  }
  return `${row.key}: ${parts.join(', ')}`;
}

/**
 * Today's bids or offers (org.eggs.get_bo, 'B' or 'O') as rows, the same way as the trades: one
 * per key, busiest first then in the board's order, the member's own ones marked (get_bo keeps
 * MemberID only on the caller's own entries), and a market-based one as an empty circle at its
 * flat price. A bid's price is delivered, so its rows are on the sale price; an offer's is at the
 * seller's dock. Web-only.
 * @param {Object[]} bos get_bo's rows: EggType, EggColor, EggClass, CACompliant, BO, Price,
 *   Market, MarketRegion, MarketSize, and MemberID on the member's own.
 * @param {'B'|'O'} side
 * @returns {MarketRow[]}
 */
export function bidOfferRows(bos, side) {
  const order = new Map();   // the board's order of keys (get_bo sorts by the egg type's sort order)
  const items = (bos || []).filter(b => (b.BO || '').trim() === side).map(b => {
    const key = marketKey(b);
    if (!order.has(key)) {
      order.set(key, order.size);
    }
    return {
      CACompliant: b.CACompliant, EggType: b.EggType, EggColor: b.EggColor, EggClass: b.EggClass,
      DockPrice: b.Price, SalePrice: b.Price,
      Market: b.Market, MarketRegion: b.MarketRegion, MarketSize: b.MarketSize,
      ...(b.MemberID ? {[side === 'B' ? 'BuyerID' : 'SellerID']: b.MemberID} : {}),
      s1: order.get(key), s2: 0, s3: 0,
    };
  });
  const noun = side === 'B' ? 'bid' : 'offer';
  return marketRows(items, null, side === 'B' ? 'sale' : 'dock').map(row => ({...row, noun}));
}
